import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { clearCountryDetails } from "./countrySlice";
import {
  createCountry,
  getAllCountries,
  getCountryByID,
  updateCountry,
} from "./services/country.services";
import { closed } from "../../components/drawer/drawerSlice";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";

const CreateCountry: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const CountryDetail = useAppSelector((state) => state.country.CountryDetail);
  const CountryGUID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.country.formLoading);

  useEffect(() => {
    return () => {
      dispatch(clearCountryDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    if (CountryGUID) {
      dispatch(getCountryByID(CountryGUID));
    }
  }, [CountryGUID]);

  const handleSubmit = async (values: any) => {
    try {
      if (!CountryGUID) {
        const insertPayload = {
          CountryName: values.CountryName,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createCountry(insertPayload));
        if (response?.payload?.Status === 201) {
          dispatch(closed());
          dispatch(clearCountryDetails());
          dispatch(getAllCountries());
        }
      } else {
        const editPayload = {
          CountryGUID: CountryGUID,
          CountryName: values.CountryName,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateCountry(editPayload));
        if (response?.payload?.Status === 200) {
          dispatch(closed());
          dispatch(clearCountryDetails());
          dispatch(getAllCountries());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  if (formLoading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={CountryDetail}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement style={{ width: "100%" }}>
          <GridLayout
            style={{
              display: "flex",
              flexWrap: "wrap",
            }}
            gap={{ rows: 10 }}
            cols={[{ width: "100%" }]}
          >
            <GridLayoutItem className="drawer-header">
              <Typography.h4 className="m-0">
                {CountryGUID ? "Update Country" : "Create Country"}
              </Typography.h4>
              <Button
                type="button"
                fillMode={"flat"}
                themeColor={"dark"}
                style={{ marginRight: 4 }}
                onClick={() => dispatch(closed())}
              >
                <RxCross1 />
              </Button>
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="CountryName"
                label="Country Name"
                placeholder="i.e. India"
                component={FormTextField}
                validator={requiredValidator} astrike={true}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                id={"IsActive"}
                name={"IsActive"}
                component={FormSelectionField}
                label={"Status"}
                placeholder="Status"
                validator={requiredValidator} astrike={true}
                options={STATUSARRAY?.map((status: any) => {
                  return {
                    value: status.value,
                    label: status.label,
                  };
                })}
              />
            </GridLayoutItem>
            <GridLayoutItem
              colSpan={3}
              style={{
                marginTop: 20,
                width: "100%",
              }}
            >
              <ButtonWithLoading
                label={CountryGUID ? "Update" : "Create"}
                type="submit"
                disabled={!formRenderProps.allowSubmit || formLoading}
                loading={formLoading}
              />
              <Button
                type="button"
                fillMode={"outline"}
                themeColor={"primary"}
                style={{ marginLeft: 4 }}
                onClick={handleFormClose}
              >
                Cancel
              </Button>
            </GridLayoutItem>
          </GridLayout>
        </FormElement>
      )}
    />
  );
};

export default CreateCountry;
